/**
 * Supabase DataStore Implementation
 *
 * Uses Supabase for persistent cloud storage when user is authenticated.
 */

import { supabase } from '../supabase';
import type {
  IDataStore,
  IListsStore,
  ISongsStore,
  IPerformancesStore,
  IAuthStore,
  ServiceResult,
  SongFilters,
  SongWithStats,
  Song,
  SongInsert,
  SongUpdate,
  List,
  ListInsert,
  ListUpdate,
  Performance,
  PerformanceInsert,
  PerformanceUpdate,
  AppUser,
  AuthMode,
} from './types';

// =============================================================================
// SUPABASE LISTS STORE
// =============================================================================

class SupabaseListsStore implements IListsStore {
  async getAll(userId: string): Promise<ServiceResult<List[]>> {
    try {
      const { data, error } = await supabase
        .from('lists')
        .select('*')
        .eq('user_id', userId)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) {
        return {
          data: null,
          error: { message: error.message, code: error.code },
        };
      }

      return { data: (data ?? []) as List[], error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to fetch lists' },
      };
    }
  }

  async get(listId: string): Promise<ServiceResult<List>> {
    try {
      const { data, error } = await supabase
        .from('lists')
        .select('*')
        .eq('id', listId)
        .single();

      if (error) {
        return {
          data: null,
          error: { message: error.message, code: error.code },
        };
      }

      return { data: data as List, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to fetch list' },
      };
    }
  }

  async getDefault(userId: string): Promise<ServiceResult<List>> {
    try {
      const { data, error } = await supabase
        .from('lists')
        .select('*')
        .eq('user_id', userId)
        .eq('is_default', true)
        .single();

      if (error) {
        return {
          data: null,
          error: { message: error.message, code: error.code },
        };
      }

      return { data: data as List, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to fetch default list' },
      };
    }
  }

  async create(list: ListInsert): Promise<ServiceResult<List>> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.from('lists') as any)
        .insert(list)
        .select()
        .single();

      if (error) {
        return {
          data: null,
          error: { message: error.message, code: error.code },
        };
      }

      return { data: data as List, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to create list' },
      };
    }
  }

  async update(listId: string, updates: ListUpdate): Promise<ServiceResult<List>> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.from('lists') as any)
        .update(updates)
        .eq('id', listId)
        .select()
        .single();

      if (error) {
        return {
          data: null,
          error: { message: error.message, code: error.code },
        };
      }

      return { data: data as List, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to update list' },
      };
    }
  }

  async delete(listId: string): Promise<ServiceResult<{ success: boolean }>> {
    try {
      // Don't allow deleting the default list
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: list } = await (supabase.from('lists') as any)
        .select('is_default')
        .eq('id', listId)
        .single();

      if (list?.is_default) {
        return {
          data: null,
          error: { message: 'Cannot delete the default list', code: 'CANNOT_DELETE_DEFAULT' },
        };
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.from('lists') as any).delete().eq('id', listId);

      if (error) {
        return {
          data: null,
          error: { message: error.message, code: error.code },
        };
      }

      return { data: { success: true }, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to delete list' },
      };
    }
  }

  async count(userId: string): Promise<ServiceResult<number>> {
    try {
      const { count, error } = await supabase
        .from('lists')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (error) {
        return {
          data: null,
          error: { message: error.message, code: error.code },
        };
      }

      return { data: count ?? 0, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to count lists' },
      };
    }
  }
}

// =============================================================================
// SUPABASE SONGS STORE
// =============================================================================

class SupabaseSongsStore implements ISongsStore {
  async getAll(listId: string, filters?: SongFilters): Promise<ServiceResult<SongWithStats[]>> {
    try {
      let query = supabase.from('songs_with_stats').select('*').eq('list_id', listId);

      // Exclude soft-deleted songs by default
      if (!filters?.includeDeleted) {
        query = query.is('deleted_at', null);
      }

      // Apply filters
      if (filters?.difficulties?.length) {
        query = query.in('difficulty', filters.difficulties);
      }

      if (filters?.vocalRangeLabels?.length) {
        query = query.in('vocal_range_label', filters.vocalRangeLabels);
      }

      if (filters?.isDuet !== undefined) {
        query = query.eq('is_duet', filters.isDuet);
      }

      if (filters?.genre) {
        query = query.eq('genre', filters.genre);
      }

      if (filters?.era) {
        const decade = parseInt(filters.era, 10);
        query = query.gte('release_year', decade).lt('release_year', decade + 10);
      }

      if (filters?.searchQuery) {
        query = query.or(
          `title.ilike.%${filters.searchQuery}%,artist.ilike.%${filters.searchQuery}%`
        );
      }

      // Order by most recently created
      query = query.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        return {
          data: null,
          error: { message: error.message, code: error.code },
        };
      }

      let result = (data ?? []) as SongWithStats[];

      // Apply performance-based filters (done client-side since they need computed fields)
      if (filters?.neverSung) {
        result = result.filter((s) => s.times_performed === 0);
      }
      if (filters?.notSungIn30Days) {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        result = result.filter(
          (s) => !s.last_performed_at || new Date(s.last_performed_at) < thirtyDaysAgo
        );
      }

      return { data: result, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to fetch songs' },
      };
    }
  }

  async get(songId: string): Promise<ServiceResult<SongWithStats>> {
    try {
      const { data, error } = await supabase
        .from('songs_with_stats')
        .select('*')
        .eq('id', songId)
        .single();

      if (error) {
        return {
          data: null,
          error: { message: error.message, code: error.code },
        };
      }

      return { data: data as SongWithStats, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to fetch song' },
      };
    }
  }

  async create(song: SongInsert): Promise<ServiceResult<Song>> {
    try {
      // If song has a music service track ID, check for existing song in the same list (including soft-deleted)
      if (song.music_service && song.music_service_track_id) {
        const { data: existingSong } = await supabase
          .from('songs')
          .select('*')
          .eq('list_id', song.list_id)
          .eq('music_service', song.music_service)
          .eq('music_service_track_id', song.music_service_track_id)
          .single();

        if (existingSong) {
          // Song already exists - restore it if soft-deleted and update with new data
          const songData = existingSong as Song;

          // Prepare updates: restore from soft-delete and update metadata
          const updates: Partial<Song> = {
            deleted_at: null, // Restore if soft-deleted
            // Update with any new metadata from the add request
            ...(song.difficulty && { difficulty: song.difficulty }),
            ...(song.vocal_range_low && { vocal_range_low: song.vocal_range_low }),
            ...(song.vocal_range_high && { vocal_range_high: song.vocal_range_high }),
            ...(song.voice_type && { voice_type: song.voice_type }),
            ...(song.is_duet !== undefined && { is_duet: song.is_duet }),
            ...(song.notes && { notes: song.notes }),
          };

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data: updatedData, error: updateError } = await (supabase.from('songs') as any)
            .update(updates)
            .eq('id', songData.id)
            .select()
            .single();

          if (updateError) {
            return {
              data: null,
              error: { message: updateError.message, code: updateError.code },
            };
          }

          return { data: updatedData as Song, error: null };
        }
      }

      // No existing song found - insert new
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.from('songs') as any)
        .insert(song)
        .select()
        .single();

      if (error) {
        return {
          data: null,
          error: { message: error.message, code: error.code },
        };
      }

      return { data: data as Song, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to create song' },
      };
    }
  }

  async update(songId: string, updates: SongUpdate): Promise<ServiceResult<Song>> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.from('songs') as any)
        .update(updates)
        .eq('id', songId)
        .select()
        .single();

      if (error) {
        return {
          data: null,
          error: { message: error.message, code: error.code },
        };
      }

      return { data: data as Song, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to update song' },
      };
    }
  }

  async delete(songId: string): Promise<ServiceResult<Song>> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.from('songs') as any)
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', songId)
        .select()
        .single();

      if (error) {
        return {
          data: null,
          error: { message: error.message, code: error.code },
        };
      }

      return { data: data as Song, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to delete song' },
      };
    }
  }

  async restore(songId: string): Promise<ServiceResult<Song>> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.from('songs') as any)
        .update({ deleted_at: null })
        .eq('id', songId)
        .select()
        .single();

      if (error) {
        return {
          data: null,
          error: { message: error.message, code: error.code },
        };
      }

      return { data: data as Song, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to restore song' },
      };
    }
  }

  async count(listId: string): Promise<ServiceResult<number>> {
    try {
      const { count, error } = await supabase
        .from('songs')
        .select('*', { count: 'exact', head: true })
        .eq('list_id', listId)
        .is('deleted_at', null);

      if (error) {
        return {
          data: null,
          error: { message: error.message, code: error.code },
        };
      }

      return { data: count ?? 0, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to count songs' },
      };
    }
  }
}

// =============================================================================
// SUPABASE PERFORMANCES STORE
// =============================================================================

class SupabasePerformancesStore implements IPerformancesStore {
  async getAll(songId: string): Promise<ServiceResult<Performance[]>> {
    try {
      const { data, error } = await supabase
        .from('performances')
        .select('*')
        .eq('song_id', songId)
        .order('performed_at', { ascending: false });

      if (error) {
        return {
          data: null,
          error: { message: error.message, code: error.code },
        };
      }

      return { data: (data ?? []) as Performance[], error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to fetch performances' },
      };
    }
  }

  async create(performance: PerformanceInsert): Promise<ServiceResult<Performance>> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.from('performances') as any)
        .insert(performance)
        .select()
        .single();

      if (error) {
        return {
          data: null,
          error: { message: error.message, code: error.code },
        };
      }

      return { data: data as Performance, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to create performance' },
      };
    }
  }

  async update(
    performanceId: string,
    updates: PerformanceUpdate
  ): Promise<ServiceResult<Performance>> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.from('performances') as any)
        .update(updates)
        .eq('id', performanceId)
        .select()
        .single();

      if (error) {
        return {
          data: null,
          error: { message: error.message, code: error.code },
        };
      }

      return { data: data as Performance, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to update performance' },
      };
    }
  }

  async delete(performanceId: string): Promise<ServiceResult<{ success: boolean }>> {
    try {
      const { error } = await supabase.from('performances').delete().eq('id', performanceId);

      if (error) {
        return {
          data: null,
          error: { message: error.message, code: error.code },
        };
      }

      return { data: { success: true }, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to delete performance' },
      };
    }
  }
}

// =============================================================================
// SUPABASE AUTH STORE
// =============================================================================

class SupabaseAuthStore implements IAuthStore {
  getMode(): AuthMode {
    return 'authenticated';
  }

  async getOrCreateUser(): Promise<ServiceResult<AppUser>> {
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        return {
          data: null,
          error: { message: sessionError.message },
        };
      }

      if (!sessionData?.session?.user) {
        return {
          data: null,
          error: { message: 'No authenticated session' },
        };
      }

      const user = sessionData.session.user;
      return {
        data: {
          id: user.id,
          mode: 'authenticated',
          email: user.email,
          displayName: user.user_metadata?.full_name || user.email,
        },
        error: null,
      };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to get user' },
      };
    }
  }

  async getCurrentUser(): Promise<ServiceResult<AppUser | null>> {
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        return {
          data: null,
          error: { message: sessionError.message },
        };
      }

      if (!sessionData?.session?.user) {
        return { data: null, error: null };
      }

      const user = sessionData.session.user;
      return {
        data: {
          id: user.id,
          mode: 'authenticated',
          email: user.email,
          displayName: user.user_metadata?.full_name || user.email,
        },
        error: null,
      };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to get user' },
      };
    }
  }

  async clear(): Promise<ServiceResult<{ success: boolean }>> {
    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        return {
          data: null,
          error: { message: error.message },
        };
      }

      return { data: { success: true }, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to sign out' },
      };
    }
  }
}

// =============================================================================
// SUPABASE DATASTORE
// =============================================================================

export class SupabaseDataStore implements IDataStore {
  private _lists: SupabaseListsStore;
  private _songs: SupabaseSongsStore;
  private _performances: SupabasePerformancesStore;
  private _auth: SupabaseAuthStore;

  constructor() {
    this._lists = new SupabaseListsStore();
    this._songs = new SupabaseSongsStore();
    this._performances = new SupabasePerformancesStore();
    this._auth = new SupabaseAuthStore();
  }

  get lists(): IListsStore {
    return this._lists;
  }

  get songs(): ISongsStore {
    return this._songs;
  }

  get performances(): IPerformancesStore {
    return this._performances;
  }

  get auth(): IAuthStore {
    return this._auth;
  }

  async initialize(): Promise<void> {
    // No initialization needed for Supabase - it's ready to use
  }

  async close(): Promise<void> {
    // No cleanup needed for Supabase client
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

let instance: SupabaseDataStore | null = null;

export function getSupabaseDataStore(): SupabaseDataStore {
  if (!instance) {
    instance = new SupabaseDataStore();
  }
  return instance;
}

/**
 * Check if there's an active Supabase session.
 * This is used by getDataStore to determine which datastore to use.
 */
export async function hasSupabaseSession(): Promise<boolean> {
  try {
    const { data } = await supabase.auth.getSession();
    return !!data?.session;
  } catch {
    return false;
  }
}
